import { FC, memo, useState } from 'react';

import { Tooltip } from 'antd';

/**
 * Спарклайн — крошечная линия в ячейке таблицы: как метрика менялась по дням/неделям/месяцам.
 * Чистый SVG без echarts: в таблице их сотни, echarts на каждую ячейку был бы слишком тяжёлым.
 * При наведении показывает точку под курсором и её значение — как график над таблицей.
 */

export const SPARKLINE_WIDTH = 80;
export const SPARKLINE_HEIGHT = 22;
// Отступ линии от левого края ячейки (учитывается и в замере ширины колонки в ReportTable)
export const SPARKLINE_INDENT = 5;
// Больше точек в 80px не различить — прореживаем, чтобы не рисовать лишнее
const MAX_POINTS = 60;

// Один спокойный цвет: рост и падение видны по форме линии, цветом изменение показывает только «Динамика».
// Раньше красили по первой и последней точке — на днях последняя (сегодня, неполный день) почти всегда
// ниже, и линии выходили красными наугад
const SPARKLINE_COLOR = '#7a95b8';

interface SparklineProps {
    /** Значения по порядку периодов (дней, недель, месяцев); null — значения нет (пропуск в линии) */
    values: Array<number | null>;
}

/** Равномерное прореживание, первая и последняя точки сохраняются */
const thin = <T, >(values: T[]): T[] => {
    if (values.length <= MAX_POINTS) return values;
    const step = (values.length - 1) / (MAX_POINTS - 1);
    return Array.from({ length: MAX_POINTS }, (_, i) => values[Math.round(i * step)]);
};

const formatValue = (value: number) => value.toLocaleString('ru-RU', { maximumFractionDigits: 2 });

const SparklineInner: FC<SparklineProps> = ({ values }) => {
    // Точка под курсором: подсказка показывает только её значение (Денис: «больше не надо»)
    const [ hovered, setHovered ] = useState<number | null>(null);

    const series = thin(values).map((v) => (v !== null && Number.isFinite(v) ? v : null));
    const present = series.filter((v): v is number => v !== null);
    if (present.length === 0) return null;

    const pad = 2;
    const width = SPARKLINE_WIDTH;
    const height = SPARKLINE_HEIGHT;
    const min = Math.min(...present);
    const max = Math.max(...present);
    const range = max - min;
    const stepX = series.length > 1 ? (width - (pad * 2)) / (series.length - 1) : 0;
    const toX = (i: number) => pad + (i * stepX);
    // Все значения одинаковые — ровная линия посередине
    const toY = (v: number) => (range === 0 ? height / 2 : height - pad - (((v - min) / range) * (height - (pad * 2))));

    // Отрезки между пустыми точками не соединяем: провал в 0 выглядел бы как падение метрики
    const segments: Array<Array<{ x: number; y: number }>> = [];
    let current: Array<{ x: number; y: number }> = [];
    series.forEach((v, i) => {
        if (v === null) {
            if (current.length > 0) segments.push(current);
            current = [];
            return;
        }
        current.push({ x: toX(i), y: toY(v) });
    });
    if (current.length > 0) segments.push(current);

    const color = SPARKLINE_COLOR;
    const lastSegment = segments[segments.length - 1];
    const last = lastSegment[lastSegment.length - 1];

    // Ближайшая к курсору точка со значением
    const handleMove = (event: React.MouseEvent<SVGSVGElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const ratio = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0;
        const raw = stepX > 0 ? Math.round(((ratio * width) - pad) / stepX) : 0;
        const index = Math.min(series.length - 1, Math.max(0, raw));
        setHovered(series[index] === null ? null : index);
    };

    const hoveredValue = hovered !== null ? series[hovered] : null;

    return (
        <Tooltip
            title={hoveredValue !== null ? formatValue(hoveredValue) : ''}
            open={hoveredValue !== null}
            placement="top"
            mouseEnterDelay={0}
            mouseLeaveDelay={0}
        >
            <svg
                className="report-sparkline"
                width={width}
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                style={{ marginLeft: SPARKLINE_INDENT }}
                onMouseMove={handleMove}
                onMouseLeave={() => setHovered(null)}
            >
                {segments.map((segment, index) => (segment.length > 1 ? (
                    <polyline
                        key={index}
                        points={segment.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ')}
                        fill="none"
                        stroke={color}
                        strokeWidth={1.5}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                    />
                ) : (
                    // Одиночная точка между пропусками — маленький кружок, чтобы она не потерялась
                    <circle key={index} cx={segment[0].x.toFixed(1)} cy={segment[0].y.toFixed(1)} r={1} fill={color} />
                )))}
                <circle cx={last.x.toFixed(1)} cy={last.y.toFixed(1)} r={1.8} fill={color} />
                {hoveredValue !== null && hovered !== null && (
                    <circle
                        cx={toX(hovered).toFixed(1)}
                        cy={toY(hoveredValue).toFixed(1)}
                        r={2.6}
                        fill="#fff"
                        stroke={color}
                        strokeWidth={1.5}
                    />
                )}
            </svg>
        </Tooltip>
    );
};

export const Sparkline = memo(SparklineInner);

/** Заглушка на месте линии, пока она грузится: той же ширины, чтобы колонка не прыгала */
const SparklineLoadingInner: FC = () => (
    <span
        className="report-sparkline report-sparkline--loading"
        style={{ width: SPARKLINE_WIDTH, marginLeft: SPARKLINE_INDENT }}
        title="Мини-график загружается…"
    />
);

export const SparklineLoading = memo(SparklineLoadingInner);

/**
 * Значок кнопок спарклайна (над таблицей и в «…» столбца): ломаная с точкой на конце, как сама линия.
 * Без осей — чтобы не путать с кнопкой графика
 */
export const SparklineIcon: FC = () => (
    <span role="img" className="anticon">
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
            <path d="M2 16l5-7l4 5l5-8l4 5" />
            <circle cx="20" cy="11" r="2" fill="currentColor" stroke="none" />
        </svg>
    </span>
);

export default Sparkline;
